<template>
  <div class="kv-ve__group">
    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-common-align") }}</label>
      <VeAlignPicker
        v-model="alignProxy"
        :label="$_('visualEditor-emailEditor-common-align')"
        :leftTitle="$_('visualEditor-emailEditor-common-left')"
        :centerTitle="$_('visualEditor-emailEditor-common-center')"
        :rightTitle="$_('visualEditor-emailEditor-common-right')"
        :allowEmpty="true"
      />
    </div>

    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-column-verticalAlignLabel") }}</label>
      <VeVerticalAlignPicker
        v-model="verticalAlignProxy"
        :label="$_('visualEditor-emailEditor-column-verticalAlignLabel')"
        :topTitle="$_('visualEditor-emailEditor-common-top')"
        :middleTitle="$_('visualEditor-emailEditor-common-middle')"
        :bottomTitle="$_('visualEditor-emailEditor-common-bottom')"
        :allowEmpty="false"
      />
      <small class="text-muted d-block mt-1">
        {{ $_("visualEditor-emailEditor-column-verticalAlignHelp") }}
      </small>
    </div>

    <div class="form-group">
      <label>{{ $_('visualEditor-emailEditor-common-padding') }}</label>
      <VeSpacingPicker
        v-model="paddingProxy"
        :y-label="$_('visualEditor-emailEditor-common-axisY')"
        :x-label="$_('visualEditor-emailEditor-common-axisX')"
        :centerLabel="$_('visualEditor-emailEditor-common-padding')"
        :min="0"
        :max="120"
        :step="1"
      />
    </div>

    <div class="form-group">
      <VeBorderSettings
        v-model="borderProxy"
        :label="$_('visualEditor-emailEditor-borderSettings-label')"
        :hideSides="true"
      />
    </div>

    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-common-corners") }}</label>
      <VeCornersPicker
        v-model="borderRadiusProxy"
        :options="borderRadiusOptions.map((o) => ({ value: o.value, title: $_(o.labelKey) }))"
      />
    </div>

    <div class="form-group">
      <VeBackgroundPicker
        v-model="backgroundPickerProxy"
        :label="$_('visualEditor-emailEditor-common-background')"
        :colorLabel="$_('visualEditor-emailEditor-backgroundPicker-color')"
        :imageLabel="$_('visualEditor-emailEditor-backgroundPicker-image')"
        :chooseImageLabel="$_('visualEditor-emailEditor-backgroundPicker-choose')"
        :removeImageLabel="$_('visualEditor-emailEditor-backgroundPicker-remove')"
        :emptyImageLabel="$_('visualEditor-emailEditor-backgroundPicker-empty')"
      />
      <small class="text-muted d-block mt-1">{{ $_("visualEditor-emailEditor-column-backgroundHelp") }}</small>
    </div>
  </div>
</template>

<script>
import VeAlignPicker from "../components/VeAlignPicker.vue";
import VeVerticalAlignPicker from "../components/VeVerticalAlignPicker.vue";
import VeSpacingPicker from "../components/VeSpacingPicker.vue";
import VeBackgroundPicker from "../components/VeBackgroundPicker.vue";
import VeBorderSettings from "../components/VeBorderSettings.vue";
import VeCornersPicker from "../components/VeCornersPicker.vue";

export default {
  name: "VeColumnSettings",
  components: { VeAlignPicker, VeVerticalAlignPicker, VeSpacingPicker, VeBackgroundPicker, VeBorderSettings, VeCornersPicker },
  props: {
    col: { type: Object, required: true },
    borderRadiusOptions: { type: Array, required: true },
  },
  computed: {
    alignProxy: {
      get() {
        const v = String(this.col?.settings?.align || "").toLowerCase();
        return v === "left" || v === "center" || v === "right" ? v : null;
      },
      set(next) {
        if (!this.col.settings) this.$set(this.col, "settings", {});
        const v = String(next || "").toLowerCase();
        const safe = v === "left" || v === "center" || v === "right" ? v : null;
        if (!safe) this.$delete(this.col.settings, "align");
        else this.$set(this.col.settings, "align", safe);
      },
    },
    verticalAlignProxy: {
      get() {
        const v = String(this.col?.settings?.verticalAlign || "top").toLowerCase();
        return v === "middle" || v === "bottom" ? v : "top";
      },
      set(next) {
        if (!this.col.settings) this.$set(this.col, "settings", {});
        const v = String(next || "top").toLowerCase();
        const safe = v === "middle" || v === "bottom" ? v : "top";
        // Keep model compact: default "top" is implied.
        if (safe === "top") this.$delete(this.col.settings, "verticalAlign");
        else this.$set(this.col.settings, "verticalAlign", safe);
      },
    },
    paddingProxy: {
      get() {
        const s = this.col?.settings || {};
        const safe = (n) => {
          const x = Number(n);
          return Number.isFinite(x) && x >= 0 ? Math.round(x) : 8;
        };
        return {
          top: safe(s.paddingTop),
          right: safe(s.paddingRight),
          bottom: safe(s.paddingBottom),
          left: safe(s.paddingLeft),
        };
      },
      set(next) {
        const v = next || {};
        const safe = (n) => {
          const x = Number(n);
          return Number.isFinite(x) ? Math.max(0, Math.round(x)) : 0;
        };
        if (!this.col.settings) this.$set(this.col, "settings", {});
        this.$set(this.col.settings, "paddingTop", safe(v.top));
        this.$set(this.col.settings, "paddingRight", safe(v.right));
        this.$set(this.col.settings, "paddingBottom", safe(v.bottom));
        this.$set(this.col.settings, "paddingLeft", safe(v.left));
      },
    },
    borderProxy: {
      get() {
        const b = this.col?.settings?.border || {};
        const sides = b.sides || {};
        return {
          enabled: Boolean(b.enabled),
          style: b.style || "solid",
          color: b.color || "#e5e7eb",
          width: Number.isFinite(Number(b.width)) ? Math.max(1, Math.min(20, Math.round(Number(b.width)))) : 1,
          sides: {
            top: sides.top !== false,
            right: sides.right !== false,
            bottom: sides.bottom !== false,
            left: sides.left !== false,
          },
        };
      },
      set(next) {
        const v = next || {};
        if (!this.col.settings) this.$set(this.col, "settings", {});
        if (!this.col.settings.border) this.$set(this.col.settings, "border", {});
        this.$set(this.col.settings.border, "enabled", Boolean(v.enabled));
        this.$set(this.col.settings.border, "style", String(v.style || "solid").toLowerCase());
        this.$set(this.col.settings.border, "color", typeof v.color === "string" && v.color ? v.color : "#e5e7eb");
        const w = Number(v.width);
        const width = Number.isFinite(w) ? Math.max(1, Math.min(20, Math.round(w))) : 1;
        this.$set(this.col.settings.border, "width", width);
        const sides = v.sides || {};
        this.$set(this.col.settings.border, "sides", {
          top: sides.top !== false,
          right: sides.right !== false,
          bottom: sides.bottom !== false,
          left: sides.left !== false,
        });
      },
    },
    borderRadiusProxy: {
      get() {
        const val = this.col?.settings?.borderRadius;
        if (val && typeof val === "object" && !Array.isArray(val)) {
          return val;
        }
        const n = Number(val ?? 0);
        return Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
      },
      set(v) {
        if (!this.col.settings) this.$set(this.col, "settings", {});
        if (v && typeof v === "object" && !Array.isArray(v) && v.topLeft !== undefined) {
          this.$set(this.col.settings, "borderRadius", v);
        } else {
          const n = Number(v);
          const next = Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
          if (!next) this.$delete(this.col.settings, "borderRadius");
          else this.$set(this.col.settings, "borderRadius", next);
        }
      },
    },
    backgroundPickerProxy: {
      get() {
        const s = this.col?.settings || {};
        const mode = String(s.backgroundMode || "").toLowerCase() === "image" ? "image" : "color";
        const color = s.backgroundColor || "#ffffff";
        const imageUrl = s.backgroundImageUrl || null;
        // Back-compat: if we have an image url but no mode, assume image.
        const inferredMode = imageUrl ? "image" : mode;
        return {
          mode: inferredMode,
          color,
          imageUrl,
          sizeMode: s.backgroundSizeMode || "cover",
          sizeUnit: s.backgroundSizeUnit || "pct",
          sizePx: s.backgroundSizePx,
          sizePct: s.backgroundSizePct,
          repeat: s.backgroundRepeat || "no-repeat",
          position: s.backgroundPosition || "center center",
        };
      },
      set(next) {
        const v = next || {};
        const mode = String(v.mode || "color").toLowerCase() === "image" ? "image" : "color";
        const color = typeof v.color === "string" && v.color ? v.color : "#ffffff";
        const imageUrl = typeof v.imageUrl === "string" && v.imageUrl ? v.imageUrl : null;
        if (!this.col.settings) this.$set(this.col, "settings", {});
        this.$set(this.col.settings, "backgroundMode", mode);
        this.$set(this.col.settings, "backgroundColor", color === "#ffffff" ? null : color);
        this.$set(this.col.settings, "backgroundImageUrl", imageUrl);

        // Extra image controls (size/repeat/position)
        const sizeModeRaw = String(v.sizeMode || "").toLowerCase();
        const sizeMode = sizeModeRaw === "contain" || sizeModeRaw === "custom" ? sizeModeRaw : "cover";
        const sizeUnitRaw = String(v.sizeUnit || "").toLowerCase();
        const sizeUnit = sizeUnitRaw === "px" ? "px" : "pct";
        const px = Number(v.sizePx);
        const sizePx = Number.isFinite(px) ? Math.min(2000, Math.max(100, Math.round(px))) : 600;
        const pct = Number(v.sizePct);
        const sizePct = Number.isFinite(pct) ? Math.min(100, Math.max(0, Math.round(pct))) : 100;
        const repeatRaw = String(v.repeat || "").toLowerCase();
        const repeat =
          repeatRaw === "repeat" || repeatRaw === "repeat-x" || repeatRaw === "repeat-y"
            ? repeatRaw
            : "no-repeat";
        const position = String(v.position || "center center");

        this.$set(this.col.settings, "backgroundSizeMode", sizeMode);
        this.$set(this.col.settings, "backgroundSizeUnit", sizeUnit);
        this.$set(this.col.settings, "backgroundSizePx", sizePx);
        this.$set(this.col.settings, "backgroundSizePct", sizePct);
        this.$set(this.col.settings, "backgroundRepeat", repeat);
        this.$set(this.col.settings, "backgroundPosition", position);
      },
    },
  },
};
</script>


